import '../styles/index.scss';
import React, { Suspense } from 'react';
import ErrorPage from './error-page';
import { createRoot } from 'react-dom/client';

import { createBrowserRouter, RouterProvider, redirect, LoaderFunction } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import used icons
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faGear,
  faGreaterThan,
  faLessThan,
  faEquals,
  faPlusMinus,
  faCheck,
  faXmark,
  faHouse,
  faUserGear,
  faArrowLeftLongToLine,
  faPenToSquare,
  faDownload,
  faTrashXmark,
  faDatabase,
  faFilter,
  faSort, faSortUp, faSortDown,
  faArrowDownUpAcrossLine,
  faFilterSlash,
  faArrowsToLine,
  faPalette,
  faCopy,
  faChevronLeft,
  faChevronRight,
  faPlus,
  faMinus,
  faExpand,
  faCheckSquare,
  faSquare,
  faExchangeAlt,
  faTableColumns,
  faBars,
  faArrowRotateLeft,
  faArrowRightFromBracket,
} from '@fortawesome/pro-solid-svg-icons';

import {
  faCopyright, faCircleDot, faTable, faScrewdriverWrench, faArrowRightToBracket,
  faTimeline, faMagnifyingGlass,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faArrowUpRightFromSquare,
  faSquareQuestion,
  faChartScatter,
  faFile,
  faFilePdf,
  faFileCode,
  faFolder,
} from '@fortawesome/pro-regular-svg-icons';

import { LoadingAnimation } from './components/loading-page';
import { RootLayout, MinimalLayout } from './components/RootLayout';

// Dynamically import applications for ease of code splitting.
// Generally dynamic imports add lots of complexity to an application,
// however here is a good splitting point since separate views should be
// distinct in user interaction and mindset.
const DetailedItem = React.lazy(
  async () => ({
    default: (await import('./detailed-item')).DetailedItem,
  })
);
const Collection = React.lazy(
  async () => ({
    default: (await import ('./components/collection')).Collection,
  })
);
const Login = React.lazy(() => import('./login'));
const Vetting = React.lazy(() => import('./vettingApp'));
const CollectionTree = React.lazy(() => import('./components/collections'));
const ModernCollections = React.lazy(() => import('./components/modern-collections'));
const Search = React.lazy(() => import('./components/search'));
const Help = React.lazy(() => import('./components/help/index'));

library.add(faDownload, faTable, faGear, faGreaterThan, faLessThan, faEquals, faPlusMinus, faCheck, faArrowLeftLongToLine, faXmark, faCopyright, faCircleDot, faHouse, faUserGear, faScrewdriverWrench, faArrowRightToBracket, faArrowRightFromBracket, faTimeline, faPenToSquare, faMagnifyingGlass, faMagnifyingGlassPlus, faMagnifyingGlassMinus, faArrowUpRightFromSquare, faSquareQuestion, faSort, faSortUp, faSortDown, faTrashXmark, faChartScatter, faDatabase, faFilter, faArrowDownUpAcrossLine, faFilterSlash, faArrowsToLine, faPalette, faCopy, faFile, faFilePdf, faFileCode, faFolder, faChevronLeft, faChevronRight, faPlus, faMinus, faExpand, faCheckSquare, faSquare, faExchangeAlt, faTableColumns, faBars, faArrowRotateLeft);

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RootLayout>
        <ModernCollections />
      </RootLayout>
    ),
    errorElement: <RootLayout><ErrorPage /></RootLayout>,
  },
  {
    path: "/search",
    element: (
      <RootLayout>
        <Search />
      </RootLayout>
    ),
  },
  {
    path: "/login",
    element: (
      <MinimalLayout>
        <Suspense fallback={<LoadingAnimation />}><Login /></Suspense>
      </MinimalLayout>
    ),
    errorElement: <MinimalLayout><ErrorPage /></MinimalLayout>,
  },
  {
    path: "/collections/:pk",
    element: (
      <RootLayout>
        <Collection />
      </RootLayout>
    ),
    errorElement: <RootLayout><ErrorPage /></RootLayout>
  },
  {
    path: "/items/:pk",
    element: (
      <RootLayout>
        <DetailedItem />
      </RootLayout>
    ),
    errorElement: <RootLayout><ErrorPage /></RootLayout>
  },
  {
    path: "/vettings/:pk",
    element: (
      <RootLayout>
        <Vetting />
      </RootLayout>
    ),
    errorElement: <RootLayout><ErrorPage /></RootLayout>
  },
  {
    path: "/help",
    element: (
      <RootLayout>
        <Help />
      </RootLayout>
    ),
  },
  // Describe old TEV2 Paths
  {
    path: "/data/search/",
    loader: (() => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.size === 0) {
        return redirect(`/search/`);
      }
      // Pass along any search parameters
      return redirect(`/search/?${urlParams.toString()}`);
    }) as LoaderFunction,
  },
  {
    path: "/data/collection/:pk",
    loader: (({ params }) => redirect(`/collections/${params.pk}`)) as LoaderFunction,
  },
  {
    path: "/data/:resource/:pk",
    loader: (({ params }) => redirect(`/items/${params.pk?.slice(1)}`)) as LoaderFunction,
  },
]);

const client = new QueryClient();

const rootElement = document.getElementById('app');
if (!rootElement) {
  throw new Error('Failed to find the app element');
}

const root = createRoot(rootElement);

root.render(
  <QueryClientProvider client={client}>
    <RouterProvider router={router} />
  </QueryClientProvider>
);
